<template>
  <div>
    <!--begin::Card-->
    <div class="card card-custom" style="background-color: #fcfcfc">
      <!--begin::Header-->
      <div class="card-header py-3">
        <div class="card-title align-items-start flex-column">
          <h3 class="card-label font-weight-bolder text-dark">
            {{ $t("changePassword.title") }}
          </h3>
          <span class="text-muted font-weight-bold font-size-sm mt-1">{{
            $t("changePassword.description")
          }}</span>
        </div>
        <div class="card-toolbar">
          <v-btn class="btn-cancel" type="reset" @click="cancel()">
            <v-icon>mdi-close-circle</v-icon>
            {{ $t("changePassword.cancel") }}
          </v-btn>
          <v-btn
            ref="kt_save_changes"
            class="btn btn-update mr-2"
            type="submit"
            @click="save()"
          >
            <v-icon>update</v-icon>
            {{ $t("changePassword.update") }}
          </v-btn>
        </div>
      </div>
      <!--end::Header-->
      <!--begin::Form-->
      <form id="kt_password_change_form" class="form">
        <div class="card-body">
          <!--begin::Alert-->
          <div
            class="alert alert-custom alert-light-warning fade show mb-10"
            role="alert"
          >
            <div class="alert-icon">
              <span class="svg-icon svg-icon-3x svg-icon-danger">
                <!--begin::Svg Icon | path:assets/media/svg/icons/Code/Info-circle.svg-->
                <svg
                  height="24px"
                  version="1.1"
                  viewBox="0 0 24 24"
                  width="24px"
                  xmlns="http://www.w3.org/2000/svg"
                  xmlns:xlink="http://www.w3.org/1999/xlink"
                >
                  <g
                    fill="none"
                    fill-rule="evenodd"
                    stroke="none"
                    stroke-width="1"
                  >
                    <rect height="24" width="24" x="0" y="0" />
                    <circle
                      cx="12"
                      cy="12"
                      fill="#000000"
                      opacity="0.3"
                      r="10"
                    />
                    <rect
                      fill="#000000"
                      height="7"
                      rx="1"
                      width="2"
                      x="11"
                      y="10"
                    />
                    <rect
                      fill="#000000"
                      height="2"
                      rx="1"
                      width="2"
                      x="11"
                      y="7"
                    />
                  </g>
                </svg>
                <!--end::Svg Icon-->
              </span>
            </div>
            <div class="alert-text font-weight-bold">
              <ol>
                <li class="mx-1 my-2">
                  {{ $t("changePassword.passwordInstructions.forgotPassword") }}
                </li>
                <li class="mx-1 my-2">
                  {{
                    $t(
                      "changePassword.passwordInstructions.passwordRequirements"
                    )
                  }}
                </li>
                <li class="mx-1 my-2">
                  {{
                    $t("changePassword.passwordInstructions.passwordPrivacy")
                  }}
                </li>
                <li class="mx-1 my-2">
                  {{
                    $t("changePassword.passwordInstructions.keyboardLanguage")
                  }}
                </li>
              </ol>
            </div>
            <div class="alert-close">
              <button
                aria-label="Close"
                class="close"
                data-dismiss="alert"
                type="button"
              >
                <span aria-hidden="true">
                  <i class="ki ki-close"></i>
                </span>
              </button>
            </div>
          </div>
          <!--end::Alert-->
          <div class="form-group row">
            <label class="col-xl-3 col-lg-3 col-form-label text-alert">{{
              $t("changePassword.currentPassword")
            }}</label>
            <div class="col-lg-9 col-xl-6">
              <v-text-field
                v-model="password"
                :append-icon="showCurrentPass ? 'mdi-eye' : 'mdi-eye-off'"
                :error-messages="errorsPass.current_password"
                :rules="[rules.required, rules.min]"
                :type="showCurrentPass ? 'text' : 'password'"
                counter
                dense
                :label="$t('changePassword.currentPassword')"
                maxlength="255"
                outlined
                @click:append="showCurrentPass = !showCurrentPass"
              />
              <button
                class="btn btn-update text-sm btn-sm font-weight-bold"
                type="button"
                @click="forgetPassword"
              >
                <span class="fa fa-key icon-md"></span>
                {{ $t("changePassword.addOrForgotPassword") }}
              </button>
            </div>
          </div>
          <div class="form-group row">
            <label class="col-xl-3 col-lg-3 col-form-label text-alert">{{
              $t("changePassword.newPassword")
            }}</label>
            <div class="col-lg-9 col-xl-6">
              <v-text-field
                v-model="newPassword"
                :append-icon="showNewPass ? 'mdi-eye' : 'mdi-eye-off'"
                :error-messages="errorsPass.password"
                :rules="[rules.required, rules.min]"
                :type="showNewPass ? 'text' : 'password'"
                counter
                dense
                :label="$t('changePassword.newPassword')"
                maxlength="255"
                outlined
                @click:append="showNewPass = !showNewPass"
              />
            </div>
          </div>
          <div class="form-group row">
            <label class="col-xl-3 col-lg-3 col-form-label text-alert">{{
              $t("changePassword.confirmNewPassword")
            }}</label>
            <div class="col-lg-9 col-xl-6">
              <v-text-field
                v-model="confirmPassword"
                :append-icon="showRepeatPass ? 'mdi-eye' : 'mdi-eye-off'"
                :error-messages="errorsPass.confirm_password"
                :rules="[rules.required, rules.min, rules.equal]"
                :type="showRepeatPass ? 'text' : 'password'"
                counter
                dense
                :label="$t('changePassword.confirmNewPassword')"
                maxlength="255"
                outlined
                @click:append="showRepeatPass = !showRepeatPass"
              />
            </div>
          </div>
        </div>
      </form>
    </div>

    <v-dialog
      v-model="showModal"
      max-width="600"
      persistent
      transition="dialog-top-transition"
    >
      <template v-slot:default="dialog">
        <v-card>
          <v-toolbar class="font-weight-bold" color="#fc6c64" dark
            >{{ $t("changePassword.changePasswordWithCode") }}
            <v-spacer />
            <v-btn
              class="ml-1 text-hover-danger text-white"
              icon
              rounded
              small
              @click="showModal = !showModal"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar>

          <v-card-text class="p-3">
            <div class="form-group row">
              <label class="col-xl-3 col-lg-3 col-form-label text-alert">{{
                $t("changePassword.enterCode")
              }}</label>
              <div class="col-lg-9 col-xl-6">
                <v-text-field
                  v-model="code"
                  :rules="[rules.required, rules.number]"
                  counter
                  dense
                  maxlength="4"
                  outlined
                  :label="$t('changePassword.enterCode')"
                />
              </div>
            </div>

            <div class="form-group row">
              <label class="col-xl-3 col-lg-3 col-form-label text-alert">{{
                $t("changePassword.newPassword")
              }}</label>
              <div class="col-lg-9 col-xl-6">
                <v-text-field
                  v-model="newPassword"
                  :append-icon="showNewPass ? 'mdi-eye' : 'mdi-eye-off'"
                  :error-messages="errors ? errors.password : ''"
                  :rules="[rules.required, rules.min]"
                  :type="showNewPass ? 'text' : 'password'"
                  counter
                  dense
                  :label="$t('changePassword.newPassword')"
                  maxlength="255"
                  outlined
                  @click:append="showNewPass = !showNewPass"
                />
              </div>
            </div>

            <div class="form-group row">
              <label class="col-xl-3 col-lg-3 col-form-label text-alert">{{
                $t("changePassword.confirmNewPassword")
              }}</label>
              <div class="col-lg-9 col-xl-6">
                <v-text-field
                  v-model="confirmPassword"
                  :append-icon="showRepeatPass ? 'mdi-eye' : 'mdi-eye-off'"
                  :error-messages="errors ? errors.confirmPassword : ''"
                  :rules="[rules.required, rules.min, rules.equal]"
                  :type="showRepeatPass ? 'text' : 'password'"
                  counter
                  dense
                  :label="$t('changePassword.confirmNewPassword')"
                  maxlength="255"
                  outlined
                  @click:append="showRepeatPass = !showRepeatPass"
                />
              </div>
            </div>
          </v-card-text>

          <v-card-actions class="justify-end">
            <v-btn class="btn-cancel" text @click="showModal = !showModal">
              <v-icon>mdi-close-circle</v-icon>
              {{ $t("changePassword.close") }}
            </v-btn>
            <v-btn class="submit-btn" text @click="changePasswordWithCode()">
              <v-icon>mdi-update</v-icon>
              {{ $t("changePassword.change") }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </template>
    </v-dialog>
  </div>
</template>

<script>
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import i18n from "@/i18n";

export default {
  name: "ChangePassword",
  i18n,
  data() {
    return {
      errors: [],
      errorsPass: [],
      password: "",
      code: "",
      showModal: false,
      newPassword: "",
      dialog: false,
      status: "",
      confirmPassword: "",
      showCurrentPass: false,
      showNewPass: false,
      showRepeatPass: false,
      valid: true,
      rules: {
        required: (value) =>
          !!value || this.$t("changePassword.rules.required"),
        min: (v) => v.length >= 8 || this.$t("changePassword.rules.min"),
        equal: () =>
          this.newPassword === this.confirmPassword ||
          this.$t("changePassword.rules.equal"),
        number: (v) =>
          Number.isInteger(Number(this.english(v))) ||
          this.$t("changePassword.rules.number"),
      },
    };
  },
  methods: {
    changePasswordWithCode() {
      this.$modal.showLoading();
      this.requestApiPost(`change-password/${this.code}`, {
        username: this.user.phone,
        password: this.english(this.newPassword),
        confirmPassword: this.english(this.confirmPassword),
      })
        .then(({ data }) => {
          this.$modal.hideLoading();
          this.showModal = false;
          this.cancel();
          this.$toast.success(this.$t("changePassword.successMessage"));
        })
        .catch(({ response }) => {
          this.$modal.hideLoading();
          if (response.status === 404)
            return this.$toast.error(this.$t("changePassword.invalidCode"));
          this.errors = response.data.errors;
          this.$toast.error(this.$t("changePassword.tooManyAttempts"));
        });
    },
    save() {
      this.$modal.showLoading();
      this.requestApiPost("update-password", {
        _method: "PUT",
        current_password: this.password,
        password: this.newPassword,
        confirm_password: this.confirmPassword,
      })
        .then(({ data }) => {
          this.errorsPass = {};
          this.$toast.success(this.$t("changePassword.successMessage"));
          window.location.reload();
        })
        .catch(({ response }) => {
          this.errorsPass = response.data.errors;
        })
        .finally(() => this.$modal.hideLoading());
    },
    forgetPassword() {
      this.$modal.showLoading();
      this.requestApiPost("forget-password", {
        username: this.user.phone,
      })
        .then(({ data }) => {
          this.showModal = true;
        })
        .catch(({ response }) => {
          if (response?.data?.message) {
            this.$toast.error(this.$t("changePassword.tooManyAttempts"));
          }
        })
        .finally(() => this.$modal.hideLoading());
    },
    cancel() {
      this.password = "";
      this.newPassword = "";
      this.confirmPassword = "";
    },
  },
  created() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: this.$t("changePassword.title") },
    ]);
  },
};
</script>
